import React, { useCallback, useMemo } from 'react'
import { usePanelPatientUid } from '../../dashboard/panelContext'
import { CardFooter, FieldSection, LabeledMultiSelect, LabeledText } from '../fieldPrimitives'
import { RACIAL_IDENTITY_OPTIONS } from './patientRecordOptions'
import { ETHNICITY_SCHEMA } from './patientRecordSchema'
import { makeFieldSetter, useCardAutosave } from './useCardAutosave'
import { usePatientRecord } from './usePatientRecord'
import { useUpdatePatientRecord } from './useUpdatePatientRecord'

type EthnicityValues = { racialIdentities: string[]; otherRacialIdentity: string }

const EthnicityPanel = () => {
  const uid = usePanelPatientUid()
  const { loading, patient } = usePatientRecord(uid)
  const update = useUpdatePatientRecord(uid)

  const initial = useMemo<EthnicityValues>(
    () => ({
      racialIdentities: patient?.racialIdentities ?? [],
      otherRacialIdentity: patient?.otherRacialIdentity ?? '',
    }),
    [patient?.racialIdentities, patient?.otherRacialIdentity]
  )

  const save = useCallback(
    (next: EthnicityValues) =>
      update({ racialIdentities: next.racialIdentities, otherRacialIdentity: next.otherRacialIdentity }, undefined),
    [update]
  )

  const { values, setValues, error, status } = useCardAutosave(initial, ETHNICITY_SCHEMA, save)
  const set = makeFieldSetter(setValues)

  const isOtherSelected = values.racialIdentities.includes('OTHER')

  return (
    <FieldSection>
      <LabeledMultiSelect
        label="Racial identity"
        options={RACIAL_IDENTITY_OPTIONS}
        value={values.racialIdentities}
        onChange={set('racialIdentities')}
      />
      {isOtherSelected && (
        <LabeledText
          label="Other racial identity"
          value={values.otherRacialIdentity}
          onChange={set('otherRacialIdentity')}
          multiline
        />
      )}
      <CardFooter loading={loading} status={status} error={error} />
    </FieldSection>
  )
}

export default EthnicityPanel
